
import { useState, useEffect, useCallback } from 'react'
import { ServiceFactory } from '@/services/ServiceFactory'

// Simplified location interface for Places API
export interface UserLocation {
  placeId: string
  description: string
  mainText: string
  secondaryText: string
  coordinate: {
    latitude: number
    longitude: number
  }
}

const USER_LOCATION_STORAGE_KEY = 'meiuca_user_location'

export function useUserLocation() {
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') {
      setIsLoading(false)
      return
    }

    // Load user location from localStorage
    const loadUserLocation = async () => {
      try {
        console.log('🔍 Loading user location from localStorage...')
        
        const storedLocation = localStorage.getItem(USER_LOCATION_STORAGE_KEY)
        console.log('📦 Stored location:', storedLocation)
        
        if (storedLocation) {
          try {
            const parsedLocation = JSON.parse(storedLocation) as UserLocation
            console.log('✅ Parsed location:', parsedLocation)
            
            // Se não tem coordenadas, busca automaticamente
            if (!parsedLocation.coordinate || 
                (parsedLocation.coordinate.latitude === 0 && parsedLocation.coordinate.longitude === 0)) {
              console.log('🔍 UserLocation sem coordenadas, buscando automaticamente...')
              
              try {
                const addressService = ServiceFactory.createAddressService()
                const addressDetails = await addressService.getAddressDetails(parsedLocation.placeId)
                
                // Atualiza com as coordenadas obtidas
                const updatedLocation: UserLocation = {
                  ...parsedLocation,
                  coordinate: {
                    latitude: addressDetails.coordinates?.lat || 0,
                    longitude: addressDetails.coordinates?.lng || 0
                  }
                }
                
                console.log('✅ Coordenadas obtidas e atualizadas:', updatedLocation.coordinate)
                
                // Salva a versão atualizada
                localStorage.setItem(USER_LOCATION_STORAGE_KEY, JSON.stringify(updatedLocation))
                setUserLocation(updatedLocation)
              } catch (error) {
                console.error('❌ Erro ao buscar coordenadas:', error)
                // Usa a localização sem coordenadas mesmo assim
                setUserLocation(parsedLocation)
              }
            } else {
              setUserLocation(parsedLocation)
            }
          } catch (parseError) {
            console.error('❌ Error parsing stored location JSON:', parseError)
            // Remove dados corrompidos do localStorage
            localStorage.removeItem(USER_LOCATION_STORAGE_KEY)
            setUserLocation(null)
          }
        } else {
          console.log('📍 No stored location found')
          setUserLocation(null)
        }
      } catch (error) {
        console.error('❌ Error loading user location:', error)
        // Garante que mesmo com erro, o componente funcione
        setUserLocation(null)
      } finally {
        setIsLoading(false)
        console.log('✅ User location loading completed')
      }
    }

    loadUserLocation()

    // Listen for storage changes (quando outro tab/component muda o localStorage)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === USER_LOCATION_STORAGE_KEY) {
        try {
          const newLocation = e.newValue ? JSON.parse(e.newValue) as UserLocation : null
          console.log('🔄 Storage changed, new location:', newLocation)
          setUserLocation(newLocation)
        } catch (error) {
          console.error('❌ Error parsing location from storage event:', error)
          setUserLocation(null)
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  const updateUserLocation = useCallback((location: UserLocation | null) => {
    if (typeof window === 'undefined') return
    
    try {
      if (location) {
        localStorage.setItem(USER_LOCATION_STORAGE_KEY, JSON.stringify(location))
      } else {
        localStorage.removeItem(USER_LOCATION_STORAGE_KEY)
      }
      setUserLocation(location)
    } catch (error) {
      console.error('Error updating user location:', error)
    }
  }, [])

  const clearUserLocation = useCallback(() => {
    if (typeof window === 'undefined') return
    
    try {
      localStorage.removeItem(USER_LOCATION_STORAGE_KEY)
      setUserLocation(null)
    } catch (error) {
      console.error('Error clearing user location:', error)
    }
  }, [])

  const getShortLocationName = useCallback((location: UserLocation | null): string => {
    if (!location) return 'Selecionar localização'
    
    // Retorna o mainText (nome principal do lugar)
    return location.mainText
  }, [])

  const getFullLocationName = useCallback((location: UserLocation | null): string => {
    if (!location) return 'Nenhuma localização selecionada'
    
    // Retorna a descrição completa
    return location.description
  }, [])

  return {
    userLocation,
    isLoading,
    updateUserLocation,
    clearUserLocation,
    getShortLocationName,
    getFullLocationName,
    hasLocation: !!userLocation
  }
}